"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/all";
import { useRef } from "react";

type DoubtsType = {
  id: number;
  label: string;
}[];

gsap.registerPlugin(ScrollTrigger);

export const MultiDietDoubtsCarousel = ({ doubts }: { doubts: DoubtsType }) => {
  const carouselItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const carouselWrapperRef = useRef<HTMLDivElement | null>(null);

  useGSAP(() => {
    const carouselWrapper = carouselWrapperRef.current;
    const container = containerRef.current;
    const carouselItems = carouselItemRefs.current;

    if (!carouselWrapper || !container) return;

    gsap.to(carouselWrapper, {
      yPercent: "-50",
      duration: 5,
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top bottom",
        end: "+=3000",
        scrub: true,
      },
    });

    ScrollTrigger.create({
      trigger: container,
      start: "top bottom",
      end: "bottom top",
      scrub: true,
      onUpdate: () => {
        const cRect = container.getBoundingClientRect();
        const cMidY = cRect.top + cRect.height / 2;

        carouselItems.forEach((el) => {
          if (!el) return;
          const r = el.getBoundingClientRect();
          const midY = r.top + r.height / 2;
          const dist = Math.abs(cMidY - midY);
          const maxD = cRect.height / 2 + r.height / 2;
          const norm = gsap.utils.clamp(0, 1, dist / maxD);

          const widthPct = gsap.utils.interpolate(100, 75, norm);
          gsap.set(el, { width: `${widthPct}%` });
        });
      },
    });
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden w-[60%] h-[60vh]"
    >
      <div className="pointer-events-none absolute left-0 right-0 top-0 h-36 w-full bg-gradient-to-b from-brand-white to-transparent z-10" />
      <div className="pointer-events-none absolute right-0 left-0 bottom-0 h-36 w-full bg-gradient-to-t from-brand-white to-transparent z-10" />
      <div
        ref={carouselWrapperRef}
        className="flex flex-col gap-5 max-h-content"
      >
        {doubts.map((item, index) => (
          <div
            key={item.id}
            ref={(el: HTMLDivElement | null) => {
              carouselItemRefs.current[index] = el;
            }}
            className="border border-brand-grey2 rounded-2xl p-4 w-full self-end"
          >
            <p className="text-xl leading-7 lg:w-[500px] xl:w-[600px]">
              {item.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
